// Custom SSE transport for Chat V2 endpoint
// Streams AI SDK v5 events from Express Chat V2

export interface ChatV2Event {
  event: string;
  data: any;
}

export interface StreamChatV2Options {
  url: string;
  payload: any;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * Streams chat responses from the Chat V2 endpoint using SSE.
 * Yields typed events for the React layer to consume and render.
 *
 * Events emitted:
 * - response.delta: { type: 'text-delta', textDelta: string }
 * - tool_call: { type: 'tool-call', toolCallId, toolName, args }
 * - tool_result: { type: 'tool-result', toolCallId, result }
 * - tool_error: { type: 'tool-error', toolCallId, error }
 * - response.completed: { type: 'finish', finishReason, usage }
 */
export async function* streamChatV2({
  url,
  payload,
  signal,
  headers = {},
}: StreamChatV2Options): AsyncGenerator<ChatV2Event> {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...headers,
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok) {
    throw new Error(`Chat V2 request failed: ${res.status} ${res.statusText}`);
  }

  if (!res.body) {
    throw new Error("Response body is null");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const chunk of parts) {
        const lines = chunk.split("\n");
        const eventLine = lines.find((l) => l.startsWith("event:")) || "";
        const dataLine = lines.find((l) => l.startsWith("data:")) || "";

        const event = eventLine.replace("event: ", "").trim();
        const dataStr = dataLine.replace("data: ", "");

        if (dataStr === "[DONE]") {
          return;
        }

        try {
          const data = JSON.parse(dataStr);
          yield { event, data };
        } catch (error) {
          console.warn(
            "[chatV2Transport] Failed to parse SSE data:",
            dataStr,
            error,
          );
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
